import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import {
  ArrowRight,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle,
  Users,
  ExternalLink,
  Play,
} from "lucide-react";

/* ─────────────────────────────────────────────
 ICON MAP (resolve JSON string → component)
───────────────────────────────────────────── */
const ICON_MAP = {
  Calendar,
  CheckCircle,
  Users,
  Handshake: Users, // fallback
};

/* ─────────────────────────────────────────────
 PARTICLE (single floating orb)
───────────────────────────────────────────── */
const Particle = ({ style }) => (
  <div className="absolute rounded-full pointer-events-none" style={style} />
);

/* ─────────────────────────────────────────────
 SLIDE COUNTER e.g."01 / 03"
───────────────────────────────────────────── */
const SlideCounter = ({ current, total }) => (
  <div className="flex items-center gap-2 font-mono text-xs text-slate-600">
    <span className="text-primary-600 font-semibold">
      {String(current + 1).padStart(2, "0")}
    </span>
    <span className="text-slate-400">/</span>
    <span>{String(total).padStart(2, "0")}</span>
  </div>
);

/* ─────────────────────────────────────────────
 SECTOR TICKER (top horizontal marquee)
───────────────────────────────────────────── */
const SectorTicker = ({ sectors }) => {
  const repeated = [...sectors, ...sectors, ...sectors];
  return (
    <div className="overflow-hidden py-2">
      <div className="flex items-center gap-6 animate-marquee whitespace-nowrap">
        {repeated.map((s, i) => (
          <span key={i} className="flex items-center gap-3 flex-shrink-0">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-700">
              {s}
            </span>
            <span className="w-1 h-1 rounded-full bg-primary-400/60" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 SCROLL CUE (bottom center)
───────────────────────────────────────────── */
const ScrollCue = ({ text }) => {
  const lineRef = useRef(null);

  useEffect(() => {
    gsap.to(lineRef.current, {
      scaleY: 0,
      transformOrigin: "top center",
      duration: 0.8,
      ease: "power2.inOut",
      repeat: -1,
      yoyo: true,
      delay: 0.4,
    });
  }, []);

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-slate-500 text-xs font-medium tracking-[0.3em] uppercase text-slate-500">
        {text}
      </span>
      <div className="flex flex-col items-center gap-1">
        <div
          ref={lineRef}
          className="w-px h-8 bg-gradient-to-b from-primary-500 to-transparent"
        />
        <ArrowDown size={12} className="text-slate-500 animate-bounce" />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 STAT BADGE
───────────────────────────────────────────── */
const StatBadge = ({ stat, index }) => {
  const ref = useRef(null);
  const IconC = ICON_MAP[stat.icon] || Users;

  /* float loop offset per badge */
  useEffect(() => {
    gsap.to(ref.current, {
      y: index % 2 === 0 ? -8 : 8,
      duration: 2.5 + index * 0.4,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
      delay: index * 0.3,
    });
  }, [index]);

  return (
    <div
      ref={ref}
      className="glass rounded-2xl px-4 py-3 flex items-center gap-3
 border border-slate-200 hover:border-primary-300
 transition-colors duration-300 cursor-default bg-white/80 backdrop-blur-sm"
    >
      <div className="w-8 h-8 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
        <IconC size={14} className="text-primary-600" />
      </div>
      <div>
        <p className="text-sm font-black text-slate-900 leading-none">
          {stat.value}
        </p>
        <p className="text-[10px] text-slate-600 mt-0.5">{stat.label}</p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 ANIMATED BG IMAGE (per slide)
───────────────────────────────────────────── */
const BgImage = ({ src, isActive, index }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (isActive) {
      gsap.fromTo(
        ref.current,
        { opacity: 0, scale: 1.08 },
        { opacity: 1, scale: 1.03, duration: 1.6, ease: "power2.out" },
      );
      /* Ken Burns effect while active */
      gsap.to(ref.current, {
        scale: 1.08,
        duration: 8,
        ease: "none",
        delay: 1.6,
      });
    } else {
      gsap.to(ref.current, {
        opacity: 0,
        scale: 1,
        duration: 0.8,
        ease: "power2.in",
      });
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      className="absolute inset-0 will-change-transform"
      style={{ opacity: 0 }}
    >
      <img
        src={src}
        alt={`slide-${index}`}
        className="w-full h-full object-cover"
        loading={index === 0 ? "eager" : "lazy"}
      />
      {/* Dark overlay - matching services hero */}
      <div
        className="absolute inset-0 opacity-100 transition-opacity duration-500"
        style={{
          background: `
 linear-gradient(to right, rgba(6,13,36,0.96) 0%, rgba(6,13,36,0.7) 45%, rgba(6,13,36,0.3) 100%),
 linear-gradient(to top, rgba(6,13,36,0.96) 0%, transparent 40%),
 linear-gradient(to bottom, rgba(6,13,36,0.5) 0%, transparent 30%)
`,
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 SLIDE CONTENT (headline + desc + CTAs)
───────────────────────────────────────────── */
const SlideContent = ({ slide, isActive }) => {
  const containerRef = useRef(null);
  const labelRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const descRef = useRef(null);
  const ctaRef = useRef(null);
  const statRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    /* Kill previous timeline */
    if (tlRef.current) tlRef.current.kill();

    if (isActive) {
      tlRef.current = gsap.timeline();

      tlRef.current
        .fromTo(
          line1Ref.current,
          { y: 60, opacity: 0, skewY: 3 },
          { y: 0, opacity: 1, skewY: 0, duration: 0.8, ease: "power4.out" },
        )
        .fromTo(
          line2Ref.current,
          { y: 60, opacity: 0, skewY: 3 },
          { y: 0, opacity: 1, skewY: 0, duration: 0.8, ease: "power4.out" },
          "-=0.6",
        )
        .fromTo(
          descRef.current,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.2",
        )
        .fromTo(
          ctaRef.current?.children ?? [],
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.12,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.1",
        )
        .fromTo(
          statRef.current,
          { x: -20, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
          "-=0.1",
        );
    } else {
      gsap.to(containerRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.4,
        ease: "power3.in",
      });
    }
  }, [isActive]);

  /* Reset opacity when becoming active */
  useEffect(() => {
    if (isActive && containerRef.current) {
      gsap.set(containerRef.current, { opacity: 1, y: 0 });
    }
  }, [isActive]);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 flex flex-col justify-center
 px-6 sm:px-12 lg:px-20 xl:px-28
 transition-none pointer-events-none
 ${isActive ? "pointer-events-auto z-10" : "z-0"}`}
      style={{ opacity: isActive ? 1 : 0 }}
    >
      <div className="max-w-2xl xl:max-w-3xl space-y-6 pt-24 lg:pt-0">
        {/* Slide label badge - removed */}

        {/* Main headline */}
        <div className="space-y-1 overflow-hidden">
          <div ref={line1Ref} className="overflow-hidden">
            <h1
              className="font-display font-black leading-none text-white"
              style={{
                fontSize: "clamp(2.8rem, 7vw, 6rem)",
                letterSpacing: "-0.02em",
              }}
            >
              {slide.tagline}
            </h1>
          </div>
          <div ref={line2Ref} className="overflow-hidden">
            <h1
              className="font-display font-black leading-none text-gradient-primary"
              style={{
                fontSize: "clamp(2.8rem, 7vw, 6rem)",
                letterSpacing: "-0.02em",
              }}
            >
              {slide.tagline2}
            </h1>
          </div>
        </div>

        {/* Description */}
        <p
          ref={descRef}
          className="text-slate-300 leading-relaxed max-w-lg"
          style={{ fontSize: "clamp(0.95rem, 1.5vw, 1.1rem)" }}
        >
          {slide.description}
        </p>

        {/* CTA buttons */}
        <div ref={ctaRef} className="flex flex-wrap items-center gap-4">
          <Link
            to={slide.primaryCta.href}
            className="group relative inline-flex items-center gap-2.5
 px-7 py-4 bg-primary-600 hover:bg-primary-500
 text-white font-semibold rounded-2xl overflow-hidden
 transition-all duration-300 hover:shadow-glow-primary
 hover:-translate-y-0.5 text-sm"
          >
            {/* Shine sweep */}
            <span
              className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/15 to-transparent"
            />
            <span>{slide.primaryCta.text}</span>
            <ArrowRight
              size={15}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>

          <Link
            to={slide.secondaryCta.href}
            className="inline-flex items-center gap-2.5
 px-7 py-4 border border-slate-300 hover:border-slate-400
 text-slate-700 hover:bg-slate-50 font-medium rounded-2xl
 transition-all duration-300 hover:-translate-y-0.5 text-sm"
          >
            {slide.secondaryCta.text}
            <ExternalLink size={13} className="text-slate-500" />
          </Link>
        </div>

        {/* Mini slide stat */}
        <div ref={statRef} className="flex items-center gap-3 pt-2">
          <div className="h-px w-8 bg-secondary-500/60" />
          <span className="text-secondary-600 font-black text-2xl font-mono">
            {slide.stat.value}
          </span>
          <span className="text-slate-400 text-sm">{slide.stat.label}</span>
        </div>
      </div>
    </div>
  );
};
/* ─────────────────────────────────────────────
 MAIN HERO COMPONENT
───────────────────────────────────────────── */
const Hero = () => {
  const { hero } = homeData;
  const { slides, floatingStats, sectors, scrollCue, badge } = hero;

  const [activeSlide, setActiveSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  /* Refs */
  const heroRef = useRef(null);
  const meshRef = useRef(null);
  const particlesRef = useRef([]);
  const sidebarRef = useRef(null);
  const progressRef = useRef(null);
  const progressTlRef = useRef(null);
  const autoPlayRef = useRef(null);
  const progressLineRef = useRef([]);
  const rightPanelRef = useRef(null);
  const badgeRef = useRef(null);
  const statsRowRef = useRef(null);
  const tickerRef = useRef(null);
  const counterRef = useRef(null);

  const SLIDE_DURATION = 6000;

  /* ── Go to slide ── */
  const goToSlide = useCallback(
    (index) => {
      if (isAnimating || index === activeSlide) return;
      setIsAnimating(true);
      setActiveSlide(index);
      setTimeout(() => setIsAnimating(false), 900);
    },
    [isAnimating, activeSlide],
  );

  const nextSlide = useCallback(
    () => goToSlide((activeSlide + 1) % slides.length),
    [activeSlide, goToSlide, slides.length],
  );

  const prevSlide = useCallback(
    () => goToSlide((activeSlide - 1 + slides.length) % slides.length),
    [activeSlide, goToSlide, slides.length],
  );

  /* ── Autoplay + progress bar ── */
  useEffect(() => {
    /* Kill existing */
    if (progressTlRef.current) progressTlRef.current.kill();
    if (autoPlayRef.current) clearTimeout(autoPlayRef.current);

    /* Animate progress bar for active slide */
    if (progressLineRef.current[activeSlide]) {
      progressTlRef.current = gsap.fromTo(
        progressLineRef.current[activeSlide],
        { scaleX: 0 },
        { scaleX: 1, duration: SLIDE_DURATION / 1000, ease: "none" },
      );
    }

    /* Reset other progress bars */
    progressLineRef.current.forEach((el, i) => {
      if (i !== activeSlide && el) {
        gsap.set(el, { scaleX: i < activeSlide ? 1 : 0 });
      }
    });

    /* Schedule next */
    autoPlayRef.current = setTimeout(nextSlide, SLIDE_DURATION);

    return () => {
      if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
    };
  }, [activeSlide, nextSlide]);

  /* ── Mouse parallax for mesh ── */
  const handleMouseMove = useCallback((e) => {
    const { left, top, width, height } =
      heroRef.current?.getBoundingClientRect() ?? {};
    const x = ((e.clientX - left) / width - 0.5) * 2;
    const y = ((e.clientY - top) / height - 0.5) * 2;
    setMousePos({ x, y });

    gsap.to(meshRef.current, {
      x: x * 30,
      y: y * 20,
      duration: 1.2,
      ease: "power2.out",
    });
  }, []);

  /* ── Entry animation ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 3.5 });

      /* Badge */
      tl.fromTo(
        badgeRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
      );

      /* Right panel (stats) */
      tl.fromTo(
        rightPanelRef.current,
        { x: 60, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
        "-=0.3",
      );

      /* Sidebar */
      tl.fromTo(
        sidebarRef.current,
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7, ease: "power3.out" },
        "-=0.5",
      );

      /* Stats row bottom */
      tl.fromTo(
        statsRowRef.current?.children ?? [],
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 0.6,
          ease: "power3.out",
        },
        "-=0.3",
      );

      /* Ticker */
      tl.fromTo(
        tickerRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
        "-=0.3",
      );

      /* Particles float */
      particlesRef.current.forEach((p, i) => {
        if (!p) return;
        gsap.to(p, {
          y: `${(i % 2 === 0 ? "-" : "") + (10 + i * 3)}px`,
          x: `${(i % 3 === 0 ? "-" : "") + (5 + i * 2)}px`,
          duration: 3 + i * 0.7,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          delay: i * 0.4,
        });
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  /* ── Scroll parallax ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          const p = self.progress;
          gsap.set(meshRef.current, { y: p * 120 });
          gsap.set(sidebarRef.current, { y: p * 60, opacity: 1 - p * 1.5 });
          gsap.set(badgeRef.current, { y: p * -40, opacity: 1 - p * 2 });
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  /* Particle configs */
  const particles = [
    {
      top: "15%",
      left: "65%",
      width: 180,
      height: 180,
      color: "rgba(29,78,216,0.12)",
      blur: 60,
    },
    {
      top: "60%",
      left: "75%",
      width: 120,
      height: 120,
      color: "rgba(245,158,11,0.1)",
      blur: 40,
    },
    {
      top: "30%",
      left: "80%",
      width: 80,
      height: 80,
      color: "rgba(99,102,241,0.15)",
      blur: 30,
    },
    {
      top: "70%",
      left: "10%",
      width: 150,
      height: 150,
      color: "rgba(29,78,216,0.08)",
      blur: 50,
    },
    {
      top: "20%",
      left: "50%",
      width: 60,
      height: 60,
      color: "rgba(245,158,11,0.12)",
      blur: 20,
    },
    {
      top: "80%",
      left: "60%",
      width: 100,
      height: 100,
      color: "rgba(29,78,216,0.1)",
      blur: 35,
    },
  ];
  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative w-full overflow-hidden bg-white transition-colors duration-500"
      style={{ height: "100dvh", minHeight: "600px" }}
      onMouseMove={handleMouseMove}
    >
      {/* ═══════════════════════════════════════
 BACKGROUND LAYER
 ═══════════════════════════════════════ */}

      {/* Base gradient - light mode */}
      <div
        className="absolute inset-0 z-[1] opacity-100 transition-opacity duration-500 bg-white"
        style={{
          background: `
 linear-gradient(135deg, 
 rgb(255, 255, 255) 0%, 
 rgb(248, 250, 252) 50%, 
 rgb(239, 246, 255) 100%
 )
`,
        }}
      />

      {/* Animated mesh / blob */}
      <div
        ref={meshRef}
        className="absolute inset-0 z-[2] will-change-transform"
        style={{
          background: `
 radial-gradient(ellipse 80% 60% at 70% 40%, rgba(29,78,216,0.08) 0%, transparent 60%),
 radial-gradient(ellipse 50% 50% at 85% 70%, rgba(245,158,11,0.05) 0%, transparent 55%),
 radial-gradient(ellipse 40% 40% at 20% 80%, rgba(99,102,241,0.06) 0%, transparent 50%)
`,
        }}
      />

      {/* BG Images per slide */}
      <div className="absolute inset-0 z-[3]">
        {slides.map((slide, i) => (
          <BgImage
            key={slide.id}
            src={slide.image}
            isActive={i === activeSlide}
            index={i}
          />
        ))}
      </div>

      {/* Dot pattern overlay */}
      <div className="absolute inset-0 z-[4] dot-pattern opacity-20 pointer-events-none" />

      {/* Noise texture */}
      <div
        className="absolute inset-0 z-[4] pointer-events-none opacity-30"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating particles */}
      {particles.map((p, i) => (
        <Particle
          key={i}
          ref={(el) => (particlesRef.current[i] = el)}
          style={{
            position: "absolute",
            top: p.top,
            left: p.left,
            width: p.width,
            height: p.height,
            borderRadius: "50%",
            background: p.color,
            filter: `blur(${p.blur}px)`,
            zIndex: 5,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* ═══════════════════════════════════════
 TOP BADGE BAR
 ═══════════════════════════════════════ */}
      <div
        ref={badgeRef}
        className="absolute top-0 left-0 right-0 z-30
 flex items-center justify-between
 px-6 sm:px-12 lg:px-20 xl:px-28
 pt-24 lg:pt-28"
        style={{ opacity: 0 }}
      >
        {/* Slide counter */}
        <SlideCounter current={activeSlide} total={slides.length} />
      </div>

      {/* ═══════════════════════════════════════
 SLIDE CONTENTS
 ═══════════════════════════════════════ */}
      <div className="absolute inset-0 z-20">
        {slides.map((slide, i) => (
          <SlideContent
            key={slide.id}
            slide={slide}
            isActive={i === activeSlide}
          />
        ))}
      </div>

      {/* ═══════════════════════════════════════
 RIGHT PANEL — FLOATING STATS
 ═══════════════════════════════════════ */}
      <div
        ref={rightPanelRef}
        className="absolute right-6 sm:right-10 lg:right-16
 top-1/2 -translate-y-1/2 z-30
 hidden xl:flex flex-col gap-3"
        style={{ opacity: 0 }}
      >
        {floatingStats.map((stat, i) => (
          <StatBadge key={stat.id} stat={stat} index={i} />
        ))}
      </div>

      {/* ═══════════════════════════════════════
 LEFT SIDEBAR — SLIDE CONTROLS
 ═══════════════════════════════════════ */}
      <div
        ref={sidebarRef}
        className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-30
 hidden lg:flex flex-col items-center gap-5"
        style={{ opacity: 0 }}
      >
        {/* Prev */}
        <button
          onClick={prevSlide}
          disabled={isAnimating}
          aria-label="Previous slide"
          className="w-9 h-9 rounded-xl border border-slate-200
 hover:border-primary-300 hover:bg-primary-50
 flex items-center justify-center text-slate-600
 hover:text-primary-600 transition-all duration-300 disabled:opacity-40"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Dot indicators */}
        <div className="flex flex-col items-center gap-2.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="relative group"
            >
              <div
                className={`rounded-full transition-all duration-500 ${
                  i === activeSlide
                    ? "w-1.5 h-6 bg-secondary-500"
                    : "w-1.5 h-1.5 bg-slate-400 hover:bg-slate-600"
                }`}
              />
            </button>
          ))}
        </div>

        {/* Next */}
        <button
          onClick={nextSlide}
          disabled={isAnimating}
          aria-label="Next slide"
          className="w-9 h-9 rounded-xl border border-slate-200
 hover:border-primary-300 hover:bg-primary-50
 flex items-center justify-center text-slate-600
 hover:text-primary-600 transition-all duration-300 disabled:opacity-40"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      {/* ═══════════════════════════════════════
 BOTTOM BAND
 ═══════════════════════════════════════ */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        {/* Light mode background - updated to dark */}
        <div
          className="absolute inset-0 opacity-100 transition-opacity duration-500"
          style={{
            background:
              "linear-gradient(to top, rgba(6,13,36,0.95) 0%, transparent 100%)",
          }}
        />
        <div className="relative z-10">
          {/* Progress bars */}
          <div
            className="flex items-center gap-2
 px-6 sm:px-12 lg:px-20 xl:px-28 pb-2"
          >
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                aria-label={`Slide ${i + 1}`}
                className="flex-1 h-[2px] bg-slate-200 rounded-full overflow-hidden
 cursor-pointer"
              >
                <div
                  ref={(el) => (progressLineRef.current[i] = el)}
                  className="h-full rounded-full origin-left"
                  style={{
                    background: "linear-gradient(90deg, #3b82f6, #f59e0b)",
                    transform: "scaleX(0)",
                  }}
                />
              </button>
            ))}
          </div>

          {/* Stats row + scroll cue */}
          <div
            className="flex items-end justify-between
 px-6 sm:px-12 lg:px-20 xl:px-28 pb-6 pt-2"
          >
            {/* Floating stats — mobile (3 stats) */}
            <div
              ref={statsRowRef}
              className="flex items-center gap-3 xl:hidden flex-wrap"
            >
              {floatingStats.slice(0, 3).map((stat, i) => {
                const IconC = ICON_MAP[stat.icon] || Users;
                return (
                  <div
                    key={stat.id}
                    className="flex items-center gap-2"
                    style={{ opacity: 0 }}
                  >
                    <span className="text-secondary-600 font-black text-lg font-mono">
                      {stat.value}
                    </span>
                    <span className="text-slate-400 text-xs">{stat.label}</span>
                    {i < 2 && <span className="w-px h-4 bg-slate-300 ml-1" />}
                  </div>
                );
              })}
            </div>

            {/* Scroll cue */}
            <div className="ml-auto">
              <ScrollCue text={scrollCue} />
            </div>
          </div>

          {/* Sector ticker bar */}
          <div
            ref={tickerRef}
            className="border-t border-slate-200/20 py-2 overflow-hidden"
            style={{ opacity: 0 }}
          >
            <SectorTicker sectors={sectors} />
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
 DECORATIVE CORNER LINES
 ═══════════════════════════════════════ */}
      {/* Top-left corner */}
      <div className="absolute top-20 left-6 sm:left-12 z-20 hidden sm:block">
        <div className="w-12 h-12 border-l-2 border-t-2 border-primary-600/30 rounded-tl-lg" />
      </div>

      {/* Bottom-right corner */}
      <div className="absolute bottom-28 right-6 sm:right-12 z-20 hidden sm:block">
        <div className="w-12 h-12 border-r-2 border-b-2 border-secondary-400/30 rounded-br-lg" />
      </div>

      {/* Vertical accent line left */}
      <div
        className="absolute left-0 top-1/4 bottom-1/4 w-[2px] z-20 hidden lg:block"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(29,78,216,0.4), transparent)",
        }}
      />
    </section>
  );
};

export default Hero;
